import React from 'react';
import Layout from './Layout';
import { ArrowRight, Recycle, Factory, TrendingUp } from 'lucide-react';

const CircularWaste: React.FC = () => {
  const wasteStreams = [
    {
      type: 'Municipal Solid Waste',
      input: '2,847 tons',
      processed: '2,563 tons',
      efficiency: 90.0,
      outputs: [
        { material: 'RDF (Refuse Derived Fuel)', quantity: '1,234 tons', destination: 'Cement Kilns' },
        { material: 'Biogas', quantity: '892 MWh', destination: 'Steel EAF' },
        { material: 'Recovered Metals', quantity: '156 tons', destination: 'Steel Production' }
      ]
    },
    {
      type: 'Industrial Waste',
      input: '1,567 tons',
      processed: '1,423 tons',
      efficiency: 90.8,
      outputs: [
        { material: 'Alternative Fuel', quantity: '678 tons', destination: 'Cement Production' },
        { material: 'Scrap Steel', quantity: '234 tons', destination: 'EAF Furnaces' },
        { material: 'Organic Fraction', quantity: '511 tons', destination: 'Biogas Plant' }
      ]
    }
  ];

  const pathwayMetrics = [
    {
      title: 'Waste Diverted from Landfill',
      value: '4,414 tons',
      change: '+18.5%',
      icon: Recycle,
      color: 'text-green-600'
    },
    {
      title: 'Materials Recovered',
      value: '3,986 tons',
      change: '+22.1%',
      icon: Factory,
      color: 'text-blue-600'
    },
    {
      title: 'Circular Economy Value',
      value: '$1.2M',
      change: '+15.7%',
      icon: TrendingUp,
      color: 'text-purple-600'
    }
  ];

  const cementPathway = [
    { stage: 'MSW Collection', description: 'Municipal solid waste collection from urban areas', status: 'active' },
    { stage: 'Pre-sorting', description: 'Automated sorting to separate recyclables and organics', status: 'active' },
    { stage: 'RDF Production', description: 'Convert non-recyclable waste to refuse-derived fuel', status: 'active' },
    { stage: 'Quality Control', description: 'Ensure RDF meets cement kiln specifications', status: 'active' },
    { stage: 'Cement Integration', description: 'Replace coal with RDF in cement kilns', status: 'active' }
  ];

  const steelPathway = [
    { stage: 'Organic Waste', description: 'Organic fraction from waste sorting process', status: 'active' },
    { stage: 'Anaerobic Digestion', description: 'Biogas production from organic waste', status: 'active' },
    { stage: 'Gas Cleaning', description: 'Purification to meet steel industry standards', status: 'active' },
    { stage: 'EAF Integration', description: 'Biogas injection into electric arc furnaces', status: 'active' },
    { stage: 'Coal Displacement', description: 'Reduce coal dependency in steel production', status: 'active' }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Circular Waste → Materials Pathway</h1>
          <div className="text-sm text-gray-500">
            Circular Economy Integration
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pathwayMetrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <div className="mt-4 flex items-center">
                  <span className="text-sm font-medium text-green-600">{metric.change}</span>
                  <span className="text-sm text-gray-500 ml-2">vs last quarter</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cement Materials Pathway</h2>
            <div className="space-y-4">
              {cementPathway.map((step, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{step.stage}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  {index < cementPathway.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Steel Production Pathway</h2>
            <div className="space-y-4">
              {steelPathway.map((step, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-green-600">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{step.stage}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  {index < steelPathway.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Waste Stream Processing</h2>
          <div className="space-y-6">
            {wasteStreams.map((stream, index) => (
              <div key={index} className="border rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{stream.type}</h3>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Processing Efficiency</p>
                    <p className="text-lg font-semibold text-green-600">{stream.efficiency}%</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Input Volume</p>
                    <p className="text-xl font-semibold text-gray-900">{stream.input}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Processed Volume</p>
                    <p className="text-xl font-semibold text-green-600">{stream.processed}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Output Materials:</h4>
                  {stream.outputs.map((output, outputIndex) => (
                    <div key={outputIndex} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="font-medium text-blue-900">{output.material}</p>
                        <p className="text-sm text-blue-700">{output.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-blue-600">→ {output.destination}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Environmental Impact</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Landfill Diversion</span>
                <span className="font-semibold text-green-600">89.2%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">CO₂ Avoided</span>
                <span className="font-semibold text-green-600">3,456 tCO₂e</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Resource Recovery</span>
                <span className="font-semibold text-blue-600">90.3%</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Economic Benefits</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Cost Savings</span>
                <span className="font-semibold text-green-600">$847K</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Revenue Generated</span>
                <span className="font-semibold text-blue-600">$1.2M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">ROI</span>
                <span className="font-semibold text-purple-600">142%</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Metrics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">RDF Quality Score</span>
                <span className="font-semibold text-green-600">94/100</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Biogas Purity</span>
                <span className="font-semibold text-blue-600">97.8%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Metal Recovery</span>
                <span className="font-semibold text-purple-600">91.5%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CircularWaste;